/**
 * Pricing API service (VP-SALES-007) — wraps the backend's
 * /api/v1/pricing/price-match-requests REST surface.
 *
 * Role gates on the backend (src/routes/pricing.ts):
 *  - GET  /pricing/price-match-requests            Admin, Sales Rep, Manager
 *  - GET  /pricing/price-match-requests/:id         any authenticated user
 *  - POST /pricing/price-match-requests             Sales Rep, Customer, Admin
 *  - PATCH /pricing/price-match-requests/:id/approval  Admin, Manager
 *
 * Note: 'Manager' is not one of this app's real DB role strings (Admin,
 * Warehouse Manager, Call Center, Driver, Sales Rep, Customer) — in
 * practice only Admin can approve/deny today. The UI here gates the
 * approve/deny actions on the 'Admin' role for that reason.
 */
import { apiClient } from './api';

export type PriceMatchApprovalStatus = 'PENDING' | 'APPROVED' | 'DENIED' | 'EXPIRED';
export type PriceMatchRequesterType = 'SalesRep' | 'Customer' | 'Admin';

export interface PriceMatchRequest {
  id: number;
  requester_type: PriceMatchRequesterType;
  requester_id: number;
  sku_id: number;
  customer_id: number | null;
  requested_price: number;
  competitor_documentation_url: string | null;
  cost_of_goods: number | null;
  current_margin_percent: number | null;
  resulting_margin_percent: number | null;
  approval_status: PriceMatchApprovalStatus;
  requires_manager_approval: boolean;
  approved_by_user_id: number | null;
  approved_at: string | null;
  expiration_date: string | null;
  denial_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface ListPriceMatchFilters {
  approval_status?: PriceMatchApprovalStatus;
  requester_type?: PriceMatchRequesterType;
  sku_id?: number;
  customer_id?: number;
  limit?: number;
  offset?: number;
}

export interface ListPriceMatchResult {
  requests: PriceMatchRequest[];
  total_count: number;
  limit: number;
  offset: number;
}

export interface CreatePriceMatchInput {
  sku_id: number;
  customer_id?: number;
  requested_price: number;
  competitor_documentation_url?: string;
  cost_of_goods?: number;
  reason?: string;
}

export interface ApprovePriceMatchInput {
  approval_status: 'APPROVED' | 'DENIED';
  denial_reason?: string;
  expiration_days?: number;
}

export async function listPriceMatchRequests(filters: ListPriceMatchFilters = {}): Promise<ListPriceMatchResult> {
  // apiClient auto-unwraps the backend's `{success:true, data:[...]}` envelope
  // down to just `data` (see api.ts unwrapBody), which drops the sibling
  // total_count/limit/offset fields from that same envelope. `raw` here is
  // therefore already the plain array in the common case; the object-shaped
  // fallback covers any future response-shape change.
  const raw = await apiClient.get<any>('/pricing/price-match-requests', filters as Record<string, any>);
  const requests: PriceMatchRequest[] = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
  return {
    requests,
    total_count: raw?.total_count ?? requests.length,
    limit: raw?.limit ?? filters.limit ?? 50,
    offset: raw?.offset ?? filters.offset ?? 0,
  };
}

export async function getPriceMatchRequest(id: number): Promise<PriceMatchRequest> {
  return apiClient.get<PriceMatchRequest>(`/pricing/price-match-requests/${id}`);
}

export async function createPriceMatchRequest(input: CreatePriceMatchInput): Promise<PriceMatchRequest> {
  return apiClient.post<PriceMatchRequest>('/pricing/price-match-requests', input);
}

export async function approvePriceMatchRequest(id: number, input: ApprovePriceMatchInput): Promise<PriceMatchRequest> {
  return apiClient.patch<PriceMatchRequest>(`/pricing/price-match-requests/${id}/approval`, input);
}
